import { defaultQueryClient, clearPersistStorage } from '@shared/query-client';
import { DexieDb } from '@shared/storage';
import { FAVORITE_KEY } from '@/constants';
import { Logger } from '@shared/lib/logger';

const logger = new Logger('session-teardown');

/**
 * Wipes every piece of offline content this app stores client-side: the
 * `issues`/`articles`/`styles` Dexie tables (downloaded issues -- see
 * `entities/downloaded-issue`), the favorites bookmark list in localStorage
 * (`entities/favorite`), and the service
 * worker's image/audio/video caches (`CLEAR_OFFLINE_CONTENT`, `sw.ts`'s
 * `clearOfflineContent`).
 *
 * Why this exists at all: none of that offline content re-validates
 * against the *current* session -- it's a client-side cache of what an
 * earlier, specific login was entitled to. Without this, a reader who logs
 * out (or a different reader on a shared device who logs in) would still
 * find the previous session's downloaded/favorited content sitting right
 * there, which is exactly the entitlement leak "client-side only" implies.
 * Called on both logout (`clearTokens`, `logoutUserJwt`) and login
 * (`saveTokens`, `authenticateViaJwt`, `authenticateViaLogin`, the `token`
 * link guard) -- a fresh login must not inherit whatever the *previous*
 * session on this device left behind either.
 *
 * A fresh `DexieDb` instance rather than threading the app's live one
 * through: every call site here is outside React (auth flows, a route
 * guard), so there's no `AppContext` to read one from, and IndexedDB
 * itself supports multiple simultaneous connections to the same database
 * without issue.
 *
 * Best-effort and non-blocking like the rest of this module -- a failed
 * wipe must not fail (or even delay) the login/logout it's attached to.
 */
export async function clearOfflineContent(): Promise<void> {
	try {
		localStorage.removeItem(FAVORITE_KEY);
	} catch (err) {
		logger.error(err);
	}

	const db = new DexieDb();
	try {
		await db.start();
		await db.clear();
	} catch (err) {
		logger.error('Failed to clear offline Dexie content', err);
	} finally {
		db.close();
	}

	try {
		navigator.serviceWorker?.controller?.postMessage({
			type: 'CLEAR_OFFLINE_CONTENT',
		});
	} catch (err) {
		logger.error('Failed to message the service worker', err);
	}
}

/**
 * Longest we'll hold a logout redirect waiting on the IndexedDB purge.
 *
 * The purge is best-effort: if IndexedDB is slow, blocked by another tab's
 * open connection, or unavailable entirely (Safari private browsing), the
 * user must still get logged out and redirected. Two seconds is far above a
 * normal purge and far below "the app looks broken".
 */
const PURGE_TIMEOUT_MS = 2000;

/**
 * Drops every cached server response belonging to the session being torn down
 * — both the in-memory TanStack Query cache and the per-query IndexedDB
 * entries the persister wrote.
 *
 * Why this exists: `clearTokens()`/`logoutUserJwt()` used to clear only
 * localStorage. On the paths that redirect, the full page load happened to
 * destroy the in-memory cache, which masked the gap; on the three
 * `clearTokens()` calls in `cognito.ts` that pass no redirect, the app keeps
 * running with the previous session's articles and issues still cached. The
 * persisted IndexedDB entries survived even a reload, since they're keyed by
 * query hash and restored on the next boot.
 *
 * Whole-cache teardown rather than a targeted eviction is deliberate, and
 * matches what the call sites that already got this right do
 * (`magshop/auth/auth.hooks.tsx`'s `invalidateQueries()` + `removeQueries()`,
 * `features/login/ui/logout.tsx`'s `clearPersistStorage()`): there's no
 * per-query record of which responses were authenticated, so anything short
 * of clearing everything would be guesswork.
 *
 * Never rejects — callers chain a redirect onto it, and a failed cache purge
 * must not strand a user on a logged-out page.
 *
 * Also triggers `clearOfflineContent()` above (Dexie, favorites,
 * service-worker caches) -- a different kind of "session cache" than the
 * TanStack Query one this function is named for, but the same login/logout
 * boundary every existing caller here already sits on, so composing it in
 * here gives every one of them the fuller wipe for free rather than
 * needing a second call added at each site.
 */
export function clearSessionCache(): Promise<void> {
	// Synchronous, so the no-redirect callers are covered even if the async
	// purge below never settles.
	try {
		defaultQueryClient.clear();
	} catch (err) {
		logger.error(err);
	}

	void clearOfflineContent();

	return Promise.race([
		clearPersistStorage(),
		new Promise<void>((resolve) => setTimeout(resolve, PURGE_TIMEOUT_MS)),
	])
		.then(() => undefined)
		.catch((err) => {
			logger.error(err);
		});
}
